<template>
  <div class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg">
    <table class="w-full min-w-[1180px] table-fixed border-collapse">
      <!-- Header -->
      <thead>
        <tr class="bg-[#f0f0f4]">
          <th
            v-for="col in columns"
            :key="col.key"
            class="px-3 py-2.5 text-[13px] font-bold text-[#272735] text-center leading-tight align-middle"
            :class="col.width"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>

      <!-- Rows -->
      <tbody>
        <tr
          v-for="item in items"
          :key="item.package.id"
          class="border-t border-[#e8e8ed] bg-white hover:bg-[#fafafa] transition-colors"
        >
          <!-- Profissional que está passando -->
          <td class="px-3 py-3">
            <DoctorInfo
              :name="item.package.requester.name"
              :subtitle="`CRM ${item.package.requester.crm}`"
              :avatar-url="item.package.requester.avatarUrl"
              :nps-score="item.package.requester.npsScore"
              size="sm"
            />
          </td>

          <!-- Escala -->
          <td class="px-3 py-3">
            <span class="block text-[13px] text-[#16161d] truncate">
              {{ item.package.schedule.name }}
            </span>
          </td>

          <!-- Unidade -->
          <td class="px-3 py-3 text-center">
            <div class="flex flex-col gap-0.5 overflow-hidden">
              <span class="text-[13px] font-bold text-[#16161d] truncate">
                {{ getHealthUnitName(item) || '-' }}
              </span>
              <span v-if="getExtraHealthUnitsCount(item) > 0" class="text-[12px] text-[#7f7f8e]">
                +{{ getExtraHealthUnitsCount(item) }} unidade{{
                  getExtraHealthUnitsCount(item) > 1 ? 's' : ''
                }}
              </span>
            </div>
          </td>

          <!--
            Convidados: aceites sobre o total de convidados. Só o número de
            aceites fazia um pacote em transição parecer não ter ninguém
            convidado, quando o que faltava era resposta.
          -->
          <td class="px-3 py-3">
            <div v-if="getInviteCount(item.package) > 0" class="flex flex-col items-center gap-1">
              <span class="text-[13px] text-[#16161d]" data-testid="handover-accepted-count">
                {{ getAcceptedCount(item.package) }} de {{ getInviteCount(item.package) }}
              </span>
              <span class="text-[11px] text-[#7f7f8e]" data-testid="handover-accepted-label">
                aceitaram
              </span>
              <div
                class="h-1 w-16 overflow-hidden rounded-full bg-[#e2e2e9]"
                role="progressbar"
                :aria-valuenow="getAcceptedCount(item.package)"
                aria-valuemin="0"
                :aria-valuemax="getInviteCount(item.package)"
                :aria-label="`${getAcceptedCount(item.package)} de ${getInviteCount(
                  item.package
                )} convidados aceitaram`"
              >
                <div
                  class="h-full rounded-full transition-[width] duration-300"
                  :class="getAcceptedCount(item.package) > 0 ? 'bg-[#198038]' : ''"
                  :style="{ width: getAcceptedPercent(item.package) + '%' }"
                  data-testid="handover-accepted-progress"
                />
              </div>
            </div>
            <span
              v-else
              class="block text-center text-[13px] text-[#7f7f8e]"
              data-testid="handover-accepted-empty"
            >
              —
            </span>
          </td>

          <!-- Data do convite -->
          <td class="px-3 py-3 text-center">
            <span class="text-[13px] text-[#16161d]">
              {{ formatDate(item.package.createdAt) }}
            </span>
          </td>

          <!-- Plantões -->
          <td class="px-3 py-3 text-center">
            <span class="text-[13px] text-[#16161d]">{{ item.package.stations.length }}</span>
          </td>

          <!-- Status -->
          <td class="px-3 py-3 text-center">
            <div class="flex justify-center">
              <!--
                Com o filtro de status, a aba deixa de conter só a fila de
                aprovação. Para quem está na fila, o prazo é a informação que
                importa; para os demais, o próprio status.
              -->
              <HandoverExpirationBadge
                v-if="isInAdminQueue(item)"
                :expires-at="item.package.expiresAt"
              />
              <HandoverStatusBadge v-else :status="item.package.status" />
            </div>
          </td>

          <!-- Ações -->
          <td class="px-3 py-3 text-center">
            <!-- O OutlineButton é display:flex (bloco): text-center não o centraliza. -->
            <div class="flex justify-center">
              <!-- Só há o que avaliar enquanto o pacote está na fila do admin. -->
              <OutlineButton
                v-if="isInAdminQueue(item)"
                text="Avaliar"
                width="auto"
                height="revert-layer"
                data-testid="handover-evaluate-button"
                @click="$emit('evaluate', item)"
              />
              <!--
                Em transição não há decisão a tomar, mas há o que acompanhar: quem
                foi convidado, quem já respondeu e quais plantões estão sendo
                passados. A troca ainda está em andamento, por isso os detalhes
                abrem numa página própria e não no modal do histórico. Vale mesmo
                sem convidados — os plantões e o status continuam sendo o que
                consultar.
              -->
              <OutlineButton
                v-else-if="isInTransition(item)"
                text="Detalhes"
                width="auto"
                height="revert-layer"
                data-testid="handover-transition-details-button"
                @click="$emit('view-transition-details', item)"
              />
              <!--
                Fora da fila não há decisão a tomar, mas a troca continua tendo o
                que consultar — quem passou, quem recebeu, quais plantões. Sem esta
                ação a linha ficava com um "—" e os detalhes de uma troca aprovada
                não tinham nenhum caminho a partir desta aba.
              -->
              <OutlineButton
                v-else
                text="Detalhes"
                width="auto"
                height="revert-layer"
                data-testid="handover-detail-button"
                @click="$emit('view-details', item)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { format, parseISO } from 'date-fns'
import type { AdminQueueResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import DoctorInfo from '@/components/DoctorInfo.vue'
import { getAcceptedCount, getAcceptedPercent, getInviteCount } from '../handoverProgress'
import HandoverExpirationBadge from './HandoverExpirationBadge.vue'
import HandoverStatusBadge from './HandoverStatusBadge.vue'

defineProps<{
  items: AdminQueueResponse[]
}>()

defineEmits<{
  evaluate: [item: AdminQueueResponse]
  'view-transition-details': [item: AdminQueueResponse]
  'view-details': [item: AdminQueueResponse]
}>()

const columns = [
  { key: 'requester', label: 'Profissional que está passando', width: 'w-[230px]' },
  { key: 'schedule', label: 'Escala', width: 'w-[160px]' },
  { key: 'healthUnit', label: 'Unidade', width: 'w-[170px]' },
  { key: 'candidates', label: 'Profissionais que querem assumir', width: 'w-[150px]' },
  { key: 'inviteDate', label: 'Data do convite', width: 'w-[110px]' },
  { key: 'shifts', label: 'Plantões', width: 'w-[90px]' },
  // O header "Expiração" reflete o caso padrão da fila (badge de prazo); quando o
  // filtro de status traz pacotes fora da fila, a célula mostra o badge de status.
  { key: 'status', label: 'Expiração', width: 'w-[140px]' },
  { key: 'actions', label: 'Ações', width: 'w-[130px]' }
]

const isInAdminQueue = (item: AdminQueueResponse) =>
  item.package.status === PackageStatus.IN_ADMIN_QUEUE

// Um pacote em transição não tem o que avaliar, mas tem o que acompanhar.
const isInTransition = (item: AdminQueueResponse) => item.package.status === PackageStatus.PENDING

// Um convite pode agrupar plantões de unidades diferentes — exibimos a primeira
// e sinalizamos as demais com um contador.
const getHealthUnitNames = (item: AdminQueueResponse) => {
  const names = item.package.stations
    .map((station) => station.healthUnit?.name)
    .filter((name): name is string => !!name)
  return [...new Set(names)]
}

const getHealthUnitName = (item: AdminQueueResponse) => getHealthUnitNames(item)[0] ?? ''

const getExtraHealthUnitsCount = (item: AdminQueueResponse) =>
  Math.max(getHealthUnitNames(item).length - 1, 0)

const formatDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}
</script>
